import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, Save, X, HelpCircle,
  ChevronDown, ChevronUp, Users, Award, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../api';

const emptyQuestion = () => ({ question: '', options: ['', '', '', ''], answer: '', explanation: '' });

const QuizManager = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editQuiz, setEditQuiz] = useState(null);
  const [topicTitle, setTopicTitle] = useState('');
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [saving, setSaving] = useState(false);
  const [expandedQuiz, setExpandedQuiz] = useState(null);

  const fetchQuizzes = useCallback(async () => {
    try {
      const res = await api.get('/teacher/quizzes');
      setQuizzes(res.data || []);
    } catch { toast.error('Failed to load quizzes'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuizzes(); }, [fetchQuizzes]);

  const openCreate = () => {
    setEditQuiz(null);
    setTopicTitle('');
    setQuestions([emptyQuestion()]);
    setCreateDialog(true);
  };

  const openEdit = (quiz) => {
    setEditQuiz(quiz);
    setTopicTitle(quiz.topic_title);
    setQuestions(quiz.questions.map(q => ({
      question: q.question,
      options: [...q.options],
      answer: q.answer,
      explanation: q.explanation || '',
    })));
    setCreateDialog(true);
  };

  const addQuestion = () => {
    setQuestions([...questions, emptyQuestion()]);
  };

  const removeQuestion = (idx) => {
    if (questions.length <= 1) return;
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx, field, value) => {
    const updated = [...questions];
    updated[idx] = { ...updated[idx], [field]: value };
    setQuestions(updated);
  };

  const updateOption = (qIdx, oIdx, value) => {
    const updated = [...questions];
    updated[qIdx] = { ...updated[qIdx], options: [...updated[qIdx].options] };
    updated[qIdx].options[oIdx] = value;
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (!topicTitle.trim()) { toast.error('Enter topic title'); return; }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) { toast.error(`Q${i + 1}: Enter question text`); return; }
      if (q.options.some(o => !o.trim())) { toast.error(`Q${i + 1}: Fill all options`); return; }
      if (!q.answer.trim()) { toast.error(`Q${i + 1}: Select correct answer`); return; }
    }
    setSaving(true);
    try {
      await api.post('/teacher/quizzes', {
        topic_title: topicTitle.trim(),
        curriculum_id: editQuiz?.curriculum_id || '',
        questions,
      });
      toast.success(editQuiz ? 'Quiz updated' : 'Quiz created');
      setCreateDialog(false);
      fetchQuizzes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save quiz');
    }
    setSaving(false);
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Delete this quiz?')) return;
    try {
      await api.delete(`/teacher/quizzes/${quizId}`);
      toast.success('Quiz deleted');
      fetchQuizzes();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quiz-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="quiz-manager-title">Quiz Manager</h1>
          <p className="text-sm text-slate-500 mt-0.5">{quizzes.length} quizzes created</p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-sm font-bold" data-testid="create-quiz-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Create Quiz
        </Button>
      </div>

      {quizzes.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center">
          <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No quizzes yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first quiz for a topic</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quizzes.map((quiz) => (
            <div key={quiz.id} className="glass-card rounded-xl overflow-hidden" data-testid={`quiz-card-${quiz.id}`}>
              <button
                onClick={() => setExpandedQuiz(expandedQuiz === quiz.id ? null : quiz.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{quiz.topic_title}</p>
                    <p className="text-xs text-slate-500">{quiz.questions?.length || 0} questions</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Users className="w-3.5 h-3.5" /> {quiz.total_attempts || 0} attempts
                    </div>
                    {quiz.avg_score > 0 && (
                      <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
                        <Award className="w-3.5 h-3.5" /> Avg: {quiz.avg_score}/{quiz.questions?.length || 0}
                      </div>
                    )}
                  </div>
                  {expandedQuiz === quiz.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>

              {expandedQuiz === quiz.id && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                  <div className="space-y-2 mb-3">
                    {quiz.questions?.map((q, qi) => (
                      <div key={qi} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs font-bold text-slate-700">Q{qi + 1}: {q.question}</p>
                        <div className="grid grid-cols-2 gap-1 mt-1.5">
                          {q.options.map((opt, oi) => (
                            <span key={oi} className={`text-xs px-2 py-1 rounded ${opt === q.answer ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-white text-slate-600'}`}>
                              {String.fromCharCode(65 + oi)}. {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Student Results with Star Rating */}
                  {quiz.student_results && quiz.student_results.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-bold text-slate-700 mb-2">Student Results</p>
                      <div className="space-y-1.5">
                        {quiz.student_results.map((sr, si) => (
                          <div key={si} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`student-result-${si}`}>
                            <div>
                              <p className="text-xs font-bold text-slate-800">{sr.student_name}</p>
                              <p className="text-[10px] text-slate-500">{sr.score}/{sr.total} ({sr.percentage}%)</p>
                            </div>
                            <div className="flex items-center gap-0.5">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-4 h-4 ${i <= sr.stars ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(quiz)} className="text-xs font-bold" data-testid={`edit-quiz-${quiz.id}`}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(quiz.id)} className="text-xs font-bold text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-quiz-${quiz.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Quiz Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editQuiz ? 'Edit Quiz' : 'Create Quiz'}</DialogTitle>
            <DialogDescription>Add questions with 4 options and mark the correct answer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-bold">Topic Title</Label>
              <Input
                value={topicTitle}
                onChange={(e) => setTopicTitle(e.target.value)}
                placeholder="e.g., Animated Name"
                className="mt-1"
                data-testid="quiz-topic-input"
              />
            </div>

            {questions.map((q, qi) => (
              <div key={qi} className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid={`question-form-${qi}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-700">Question {qi + 1}</p>
                  {questions.length > 1 && (
                    <button onClick={() => removeQuestion(qi)} className="text-red-400 hover:text-red-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Input
                  value={q.question}
                  onChange={(e) => updateQuestion(qi, 'question', e.target.value)}
                  placeholder="Enter question"
                  data-testid={`q-text-${qi}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="relative">
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(qi, oi, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        className={`pr-8 ${q.answer === opt && opt ? 'border-emerald-400 bg-emerald-50' : ''}`}
                        data-testid={`q-opt-${qi}-${oi}`}
                      />
                      <button
                        onClick={() => opt && updateQuestion(qi, 'answer', opt)}
                        className={`absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${q.answer === opt && opt ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}
                        title="Mark as correct"
                        data-testid={`q-correct-${qi}-${oi}`}
                      >
                        {q.answer === opt && opt && <span className="text-white text-[10px] font-black">✓</span>}
                      </button>
                    </div>
                  ))}
                </div>
                <Input
                  value={q.explanation}
                  onChange={(e) => updateQuestion(qi, 'explanation', e.target.value)}
                  placeholder="Explanation (optional)"
                  className="text-xs"
                  data-testid={`q-explanation-${qi}`}
                />
              </div>
            ))}

            <Button onClick={addQuestion} variant="outline" className="w-full text-sm font-bold" data-testid="add-question-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Question
            </Button>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateDialog(false)} className="flex-1 text-sm">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-violet-600 hover:bg-violet-700 text-sm font-bold" data-testid="save-quiz-btn">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : editQuiz ? 'Update Quiz' : 'Create Quiz'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizManager;
